import apiClient from "./apiClient";
import { uploadFile, processProductImage } from "./uploads";
import { isAuthenticated } from "./auth";

const invalidateProductsCache = () => {
  localStorage.removeItem('products');
  localStorage.removeItem('productsTimestamp');
  // También invalidar caché de admin
  localStorage.removeItem('admin_products');
  localStorage.removeItem('admin_productsTimestamp');
};

// Función específica para invalidar caché cuando cambia la cotización del dólar
export const invalidateCacheOnDollarRateChange = () => {
  console.log('Invalidating product cache due to dollar rate change');
  invalidateProductsCache();
  // También limpiar cualquier caché de cotización del dólar en el frontend si existe
  localStorage.removeItem('dollarRate');
  localStorage.removeItem('dollarRateTimestamp');
};


// Nueva función para obtener productos con paginación
export const getProductsPaginated = async (page = 1, pageSize = 9, filters = {}) => {
  try {
    const isAuth = isAuthenticated();
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    // Construir URL con parámetros de paginación y filtros
    let url = `/products/?page=${page}&page_size=${pageSize}`;
    
    // Agregar filtros si existen
    if (filters.type && filters.type.length > 0) {
      url += `&type=${filters.type.join(',')}`;
    }
    if (filters.brands && filters.brands.length > 0) {
      url += `&brand=${filters.brands.join(',')}`;
      console.log('Adding brand filter to URL:', filters.brands);
    }
    if (filters.search) {
      url += `&search=${encodeURIComponent(filters.search)}`;
    }
    if (filters.onlyDiscounted) {
      url += `&discount=true`;
    }
    if (filters.priceRange && filters.priceRange.length > 0) {
      url += `&priceRange=${filters.priceRange.join(',')}`;
    }
    if (filters.stockFilter && filters.stockFilter.length > 0) {
      url += `&stockFilter=${filters.stockFilter.join(',')}`;
    }
    if (filters.noStock) {
      url += `&no_stock=true`;
    }
    if (filters.ordering) {
      url += `&ordering=${filters.ordering}`;
    }
    
    // Para usuarios no autenticados, agregar parámetro de filtro para productos disponibles
    if (!isAuth) {
      url += "&available=true";
    }
    
    console.log('Fetching products with URL:', url);
    console.log('Filters applied:', filters);
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    // Si el usuario no está autenticado, eliminar precios de los blisters
    if (!isAuth) {
      result.results = result.results.map(product => ({
        ...product,
        blisters: (product.blisters || []).map(blister => {
          const rest = { ...blister };
          delete rest.price;
          delete rest.discount;
          delete rest.final_price;
          return rest;
        })
      }));
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching paginated products:', error);
    throw error;
  }
};

export const getProducts = async (page = 1, pageSize = 20) => {
  try {
    const isAuth = isAuthenticated();

    console.log(`Obteniendo productos página ${page}`);
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    let url = `/products/?page=${page}&page_size=${pageSize}`;
    
    // Para usuarios no autenticados, agregar parámetro de filtro para productos disponibles
    if (!isAuth) {
      url += "&available=true";
    }
    
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    // Si el usuario no está autenticado, eliminar precios de los blisters
    if (!isAuth) {
      result.results = result.results.map(product => ({
        ...product,
        blisters: (product.blisters || []).map(blister => {
          const rest = { ...blister };
          delete rest.price;
          delete rest.discount;
          delete rest.final_price;
          return rest;
        })
      }));
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

// Función específica para admin que devuelve productos paginados sin cargar todo
export const getAllProductsForAdmin = async (page = 1, pageSize = 20, filters = {}) => {
  try {
    console.log(`Obteniendo productos página ${page} para admin con filtros:`, filters);
    const token = localStorage.getItem("accessToken");
    const headers = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    let url = `/products/?page=${page}&page_size=${pageSize}`;
    
    // Agregar filtros a la URL
    if (filters.search) {
      url += `&search=${encodeURIComponent(filters.search)}`;
    }
    if (filters.brand) {
      url += `&brand=${encodeURIComponent(filters.brand)}`;
    }
    if (filters.type) {
      url += `&type=${encodeURIComponent(filters.type)}`;
    }
    if (filters.availability) {
      if (filters.availability === 'available') {
        url += `&available=true`;
      } else if (filters.availability === 'unavailable') {
        url += `&available=false`;
      }
    }
    if (filters.stockFilter) {
      url += `&stockFilter=${encodeURIComponent(filters.stockFilter)}`;
    }
    if (filters.priceStatus) {
      url += `&priceStatus=${encodeURIComponent(filters.priceStatus)}`;
    }
    if (filters.currency) {
      url += `&currency=${encodeURIComponent(filters.currency)}`;
    }
    if (filters.noStock) {
      url += `&no_stock=true`;
    }
    
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    console.log(`Productos obtenidos para admin página ${page}: ${result.results.length}`);
    
    return result;
  } catch (error) {
    console.error('Error fetching products for admin:', error);
    throw error;
  }
};

/**
 * Subida al backend Django (`POST /api/uploads/`), igual patrón que Jamrock (`uploadFile` + subdirectory).
 */
export const uploadImage = async (file, folder = 'products') => {
  try {
    const data = await uploadFile(file, { subdirectory: folder });
    const url = data?.url ?? data?.public_url ?? data?.file_url;
    if (!url || typeof url !== 'string') {
      console.error('[uploadImage] Respuesta inesperada:', data);
      throw new Error('La API no devolvió una URL de imagen válida');
    }
    return url;
  } catch (error) {
    console.error('Error subiendo imagen:', error.response?.data || error);
    throw error instanceof Error ? error : new Error('Error al subir la imagen');
  }
};

/**
 * Sube una imagen de producto procesándola a WebP (≤ 200KB) en el servidor.
 * Devuelve la URL pública. Usar en el formulario de producto en lugar de
 * `uploadImage` para que la imagen quede optimizada.
 */
export const uploadProductImageWebp = async (file, productId = null) => {
  try {
    const data = await processProductImage(file, { productId });
    const url = data?.url ?? data?.public_url ?? data?.file_url;
    if (!url || typeof url !== 'string') {
      console.error('[uploadProductImageWebp] Respuesta inesperada:', data);
      throw new Error('La API no devolvió una URL de imagen válida');
    }
    return url;
  } catch (error) {
    console.error('Error procesando imagen:', error.response?.data || error);
    throw error instanceof Error ? error : new Error('Error al procesar la imagen');
  }
};

export const getProductsOnSale = async () => {
  try {
    const response = await apiClient.get('/products/on-sale/');
    // Verificar que la respuesta tenga la estructura correcta
    if (!response.data || !response.data.results) {
      console.warn('Unexpected API response format:', response);
      return { results: [] };
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching products on sale:', error);
    return { results: [] }; // Retornar objeto con array vacío en caso de error
  }
};

// Nueva función para obtener productos más recientes con stock y precio
export const getLatestProductsWithStock = async (limit = 8) => {
  try {
    const isAuth = isAuthenticated();
    const headers = {};
    if (isAuth) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    // Endpoint para obtener productos más recientes con stock y precio
    let url = `/products/?ordering=-created_at&has_stock=true&has_price=true&page_size=${limit}`;
    
    // Para usuarios no autenticados, agregar parámetro de filtro para productos disponibles
    if (!isAuth) {
      url += "&available=true";
    }
    
    const response = await apiClient.get(url, { headers });
    
    let result = {
      results: [],
      count: 0,
      next: null,
      previous: null
    };
    
    if (response.data && response.data.results) {
      result = response.data;
    } else if (Array.isArray(response.data)) {
      result.results = response.data;
      result.count = response.data.length;
    }
    
    // Si el usuario no está autenticado, eliminar precios de los blisters
    if (!isAuth) {
      result.results = result.results.map(product => ({
        ...product,
        blisters: (product.blisters || []).map(blister => {
          const rest = { ...blister };
          delete rest.price;
          delete rest.discount;
          delete rest.final_price;
          return rest;
        })
      }));
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching latest products with stock:', error);
    return { results: [] };
  }
};

export const getProduct = async (id) => {
  try {
    console.log('Getting product with ID:', id);
    console.log('Fetching product directly from API...');
    
    // Incluir token de autenticación si el usuario está autenticado
    const headers = {};
    if (isAuthenticated()) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    const response = await apiClient.get(`/products/${id}/`, { headers });
    console.log('Product API response:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('Error in getProduct:', error);
    console.error('Error response:', error.response?.data);
    throw new Error(error.response?.data?.message || 'Error al obtener el producto');
  }
};

// Función para crear un nuevo producto
export const createProduct = async (productData) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Creando producto:', productData);
    const response = await apiClient.post("/products/", productData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    // No hacer refresh automático del caché, dejar que se cargue cuando sea necesario
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al crear el producto');
  }
};

export const updateProduct = async (productId, productData) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Actualizando producto:', productId, productData);
    const response = await apiClient.patch(`/products/${productId}/`, productData, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    return response.data;
  } catch (error) {
    const d = error.response?.data;
    const msg =
      (d && typeof d === 'object' && (typeof d.detail === 'string' ? d.detail : d.message)) ||
      (typeof d?.error === 'string' ? d.error : null) ||
      error.message ||
      'Error al actualizar el producto';
    throw new Error(msg);
  }
};

export const deleteProduct = async (productId) => {
  try {
    const token = localStorage.getItem("accessToken");
    const response = await apiClient.delete(`/products/${productId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al eliminar el producto');
  }
};

export const getBlisterStock = async (blisterId) => {
  try {
    const response = await apiClient.get(`/products/blisters/${blisterId}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching blister stock:', error);
    throw error;
  }
};

// Función para obtener productos relacionados desde el backend
export const getRelatedProducts = async (productId, limit = 4) => {
  try {
    console.log('Getting related products for product ID:', productId);
    
    // Incluir token de autenticación si el usuario está autenticado
    const headers = {};
    if (isAuthenticated()) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    const response = await apiClient.get(`/products/${productId}/related/?limit=${limit}`, { headers });
    console.log('Related products API response:', response.data);
    
    // Si el usuario no está autenticado, eliminar precios de los blisters
    if (!isAuthenticated()) {
      const products = response.data.results || response.data;
      if (Array.isArray(products)) {
        products.forEach(product => {
          if (product.blisters && Array.isArray(product.blisters)) {
            product.blisters.forEach(blister => {
              blister.price = 0;
              blister.discount = 0;
              blister.final_price = 0;
            });
          }
        });
      }
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching related products:', error);
    return { results: [] }; // Devolver un array vacío en caso de error
  }
};

// Función para cambiar el estado de disponibilidad de un producto (solo admin)
export const toggleProductAvailability = async (productId, available) => {
  try {
    const token = localStorage.getItem("accessToken");
    console.log('Cambiando disponibilidad del producto:', productId, 'a:', available);
    const response = await apiClient.patch(`/products/${productId}/`, { available }, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      }
    });
    invalidateProductsCache();
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Error al cambiar la disponibilidad del producto');
  }
};
